import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Steps } from "antd";

import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import { useCampaignCreate } from "~/modules/Campaigns/CampaignForm/useCampaignCreate";
import { ComposeCampaignConfirm } from "./ComposeCampaignConfirm";
import ComposeCampaignForm from "./ComposeCampaignForm";
import { ComposeCampaignTarget } from "./ComposeCampaignTarget";
import { type CampaignMessageComposeFormModel, type SelectedUsersListItem } from "./types";

interface SendMessageToMultipleUsersModalProps {
  open: boolean;
  onClose: () => void;
}

enum Step {
  SelectUsers,
  WriteMessage,
  ConfirmCampaign,
}

export const CampaignForm = ({ open, onClose }: SendMessageToMultipleUsersModalProps): React.ReactElement => {
  const [currentStep, setStep] = useState(Step.SelectUsers);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUsersListItem[]>([]);
  const [formController] = Form.useForm<CampaignMessageComposeFormModel>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<string[]>([]);
  const allowedToSend = filteredProducts.length !== 0 || selectedTags.length !== 0 || selectedUsers.length !== 0;
  const [tagCondition, setTagCondition] = React.useState<"hasTag" | "notHasTag" | false>(false);
  const [filterByProducts, toggleFilterByProduct] = useState(false);
  const { loading, addNewCampaign } = useCampaignCreate(onClose);
  const [statuses, setStatuses] = useState<TraineeStatus[]>([TraineeStatus.ACTIVE]);
  const [savedModel, setSavedModel] = useState<CampaignMessageComposeFormModel | null>(null);

  useEffect(() => {
    formController.resetFields();
    if (!open) {
      setSelectedUsers([]);
      setStep(Step.SelectUsers);
      setTagCondition(false);
      setSelectedTags([]);
      setFilteredProducts([]);
      setStatuses([TraineeStatus.ACTIVE]);
      setSavedModel(null);
    }
  }, [open]);

  const steps = [Step.SelectUsers, Step.WriteMessage, Step.ConfirmCampaign];

  const addCampaign = () => {
    if (savedModel) {
      addNewCampaign(savedModel, { filteredProducts, tagCondition, selectedTags, selectedUsers, statuses });
    }
  };

  const renderStep = () => {
    if (currentStep === Step.ConfirmCampaign) {
      return <ComposeCampaignConfirm model={savedModel} condition={tagCondition} tags={selectedTags} />;
    }
    if (currentStep === Step.WriteMessage) {
      return (
        <ComposeCampaignForm
          model={savedModel ?? undefined}
          onSubmit={(model) => {
            setSavedModel(model);

            setStep(Step.ConfirmCampaign);
          }}
          formController={formController}
        />
      );
    }
    return (
      <ComposeCampaignTarget
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        changeCondition={setTagCondition}
        condition={tagCondition}
        filterByProducts={filterByProducts}
        toggleFilterByProduct={toggleFilterByProduct}
        setFilterByProducts={setFilteredProducts}
        filteredProductIds={filteredProducts}
        statuses={statuses}
        setStatuses={setStatuses}
      />
    );
  };

  return (
    <Modal
      destroyOnClose
      open={open}
      title="Zaplanuj kampanię"
      onCancel={onClose}
      width={800}
      footer={
        <div className="flex items-center justify-end gap-3">
          {currentStep > 0 && <Button onClick={() => setStep((prev) => prev - 1)}>Cofnij</Button>}
          {currentStep < steps.length - 1 && (
            <Button
              type="primary"
              onClick={() =>
                currentStep === Step.WriteMessage ? formController.submit() : setStep((prev) => prev + 1)
              }
              disabled={!allowedToSend}
            >
              Dalej
            </Button>
          )}
          {currentStep === Step.ConfirmCampaign && (
            <Button type="primary" onClick={() => addCampaign()} loading={!!loading}>
              Dodaj
            </Button>
          )}
        </div>
      }
    >
      <div className="py-3">
        <Steps
          current={currentStep}
          items={[
            {
              title: "Wybór grupy docelowej",
            },
            {
              title: "Treść kampanii",
            },
            {
              title: "Potwierdzenie",
            },
          ]}
        />
      </div>
      <div>{renderStep()}</div>
    </Modal>
  );
};
